import { useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import { articles } from '@/mocks/articles';

const categoryInfoMap: Record<string, { name: string; description: string; icon: string }> = {
  'wanghong-baoliao': { name: '网红爆料', description: '各平台网红最新黑料、翻车事件、人设崩塌全纪录', icon: 'ri-spy-line' },
  'mingxing-heitiao': { name: '明星黑料', description: '娱乐圈明星最新八卦、绯闻、争议事件追踪报道', icon: 'ri-star-smile-line' },
  'remen-zhuizong': { name: '热门追踪', description: '全网最热门吃瓜事件实时追踪与深度分析', icon: 'ri-fire-line' },
  'chigua-shipin': { name: '吃瓜视频', description: '每日最新吃瓜视频、爆料直播、热门片段合集', icon: 'ri-vidicon-line' },
  'yule-quan': { name: '娱乐圈', description: '影视圈、音乐圈、综艺圈全方位娱乐资讯', icon: 'ri-movie-line' },
  'zhibo-quan': { name: '直播圈', description: '各大直播平台主播动态、PK恩怨、带货内幕揭秘', icon: 'ri-live-line' },
};

const categoryTabs = Object.entries(categoryInfoMap).map(([slug, info]) => ({
  slug,
  ...info,
}));

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const categoryInfo = slug ? categoryInfoMap[slug] : null;

  const categoryArticles = useMemo(() => {
    if (!slug) return [];
    return articles.filter((a) => a.category === slug);
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (slug && !categoryInfo) {
      navigate('/not-found', { replace: true });
    }
  }, [slug, categoryInfo, navigate]);

  if (!categoryInfo) {
    return null;
  }

  const canonicalSlug = slug || '';

  return (
    <>
      <SEOHead
        title={`${categoryInfo.name} - 91在线吃瓜 | 最新${categoryInfo.name}资讯`}
        description={`91在线吃瓜${categoryInfo.name}频道：${categoryInfo.description}。每日更新最新${categoryInfo.name}内容，吃瓜不迷路。`}
        keywords={`${categoryInfo.name},91在线吃瓜,吃瓜,网红爆料,黑料`}
        canonicalUrl={`https://91onlinechigua.com/category/${canonicalSlug}`}
        ogType="website"
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'WebPage',
              name: `${categoryInfo.name} - 91在线吃瓜`,
              url: `https://91onlinechigua.com/category/${canonicalSlug}`,
              description: categoryInfo.description,
            },
            {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://91onlinechigua.com' },
                { '@type': 'ListItem', position: 2, name: categoryInfo.name },
              ],
            },
            {
              '@type': 'CollectionPage',
              name: `${categoryInfo.name} - 91在线吃瓜`,
              description: categoryInfo.description,
              url: `https://91onlinechigua.com/category/${canonicalSlug}`,
            },
          ],
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 pb-16">
          <div className="w-full px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-900 font-medium">{categoryInfo.name}</span>
            </nav>

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-100">
                  <i className={`${categoryInfo.icon} text-xl text-primary-600`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    {categoryInfo.name}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-0.5">{categoryInfo.description}</p>
                </div>
              </div>
              <p className="text-xs text-foreground-500">
                共 <strong className="text-primary-600">{categoryArticles.length}</strong> 条爆料
              </p>
            </div>

            <div className="flex overflow-x-auto gap-2 pb-2 mb-8 scrollbar-hide">
              {categoryTabs.map((tab) => (
                <Link
                  key={tab.slug}
                  to={`/category/${tab.slug}`}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                    slug === tab.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-600'
                  }`}
                >
                  {tab.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.slug}`}
                    className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 hover:scale-[1.01] cursor-pointer"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img
                        src={article.landscapeUrl}
                        alt={article.title}
                        title={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        {article.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                            {tag}
                          </span>
                        ))}
                      </div>
                      <h2 className="text-sm font-semibold text-foreground-900 line-clamp-2 leading-snug group-hover:text-primary-600 transition-colors mb-3">
                        {article.title}
                      </h2>
                      <div className="flex items-center justify-between text-[10px] text-foreground-500">
                        <span>{article.author}</span>
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {article.readCount > 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount} 阅读
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100">
                  <i className="ri-inbox-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-600">该分类暂无内容，敬请期待</p>
              </div>
            )}

            {categoryArticles.length > 6 && (
              <div className="mt-10 text-center">
                <button className="whitespace-nowrap px-8 py-3 rounded-full border border-primary-500 text-primary-600 text-sm font-medium hover:bg-primary-50 transition-colors duration-200 cursor-pointer">
                  加载更多
                </button>
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}